import { Dices } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Cryptographically secure random integer
function secureRandomInt(max: number): number {
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  return array[0] % max;
}

export function generateSecurePassword(length = 16): string {
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const numbers = '0123456789';
  const special = '!@#$%^&*()_+-=[]{}|;:,.<>?';

  // Guarantee at least 2 of each type
  const password: string[] = [
    uppercase[secureRandomInt(uppercase.length)],
    uppercase[secureRandomInt(uppercase.length)],
    lowercase[secureRandomInt(lowercase.length)],
    lowercase[secureRandomInt(lowercase.length)],
    numbers[secureRandomInt(numbers.length)],
    numbers[secureRandomInt(numbers.length)],
    special[secureRandomInt(special.length)],
    special[secureRandomInt(special.length)],
  ];

  // Fill remaining characters
  const all = uppercase + lowercase + numbers + special;
  while (password.length < length) {
    password.push(all[secureRandomInt(all.length)]);
  }

  // Shuffle using Fisher-Yates algorithm
  for (let i = password.length - 1; i > 0; i--) {
    const j = secureRandomInt(i + 1);
    [password[i], password[j]] = [password[j], password[i]];
  }

  return password.join('');
}

interface PasswordGeneratorButtonProps {
  onGenerate: (password: string) => void;
  disabled?: boolean;
}

export function PasswordGeneratorButton({ onGenerate, disabled }: PasswordGeneratorButtonProps) {
  const { toast } = useToast();

  const handleGenerate = async () => {
    const password = generateSecurePassword();
    onGenerate(password);

    try {
      await navigator.clipboard.writeText(password);
      toast({
        title: "Mot de passe généré",
        description: "Copié dans le presse-papier !",
      });
    } catch {
      // Clipboard might not be available
      toast({
        title: "Mot de passe généré",
        description: "Pensez à le sauvegarder dans un gestionnaire de mots de passe.",
      });
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleGenerate}
          disabled={disabled}
          className="h-8 w-8 text-muted-foreground hover:text-foreground"
          aria-label="Générer un mot de passe sécurisé"
        >
          <Dices className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>Générer un mot de passe sécurisé</p>
      </TooltipContent>
    </Tooltip>
  );
}
